import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../shadcn/ui/card';
import {
    Download,
    LogIn,
    ShieldCheck,
    Bluetooth,
    Zap,
    ClipboardList,
    Wifi,
    AlertTriangle,
    CheckCircle2,
    Smartphone,
    Apple,
    Info,
    AlertCircle,
} from 'lucide-react';
import { Cog6ToothIcon } from '@heroicons/react/24/solid';
import HGFooter from 'components/HGFooter';

const STEPS = [
    {
        number: 1,
        title: 'Download the App',
        icon: Download,
        content: (
            <div className="space-y-3">
                <div className="flex items-start gap-3 rounded-lg border border-border p-3">
                    <Apple className="mt-0.5 h-5 w-5 shrink-0 text-foreground" />
                    <div>
                        <p className="text-sm font-medium text-foreground">
                            iOS
                        </p>
                        <p className="text-sm text-secondary">
                            Download{' '}
                            <span className="font-semibold">Sync Pro</span> from
                            the App Store.
                        </p>
                    </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-border p-3">
                    <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-foreground" />
                    <div>
                        <p className="text-sm font-medium text-foreground">
                            Android
                        </p>
                        <p className="text-sm text-secondary">
                            Download{' '}
                            <span className="font-semibold">Sync Pro</span> from
                            the Google Play Store.
                        </p>
                    </div>
                </div>
            </div>
        ),
    },
    {
        number: 2,
        title: 'Log In',
        icon: LogIn,
        content: (
            <div className="space-y-2">
                {[
                    'Open the app.',
                    'Enter the email address and password provided to you by HomeGrid or your installer.',
                    'Tap Sign In.',
                ].map((item, i) => (
                    <div
                        key={i}
                        className="flex items-start gap-2 text-sm text-secondary"
                    >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
                            {i + 1}
                        </span>
                        {item}
                    </div>
                ))}
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-accent/10 p-3 text-sm">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <p className="text-secondary">
                        If you have trouble logging in, tap{' '}
                        <span className="font-semibold text-foreground">
                            Contact Support
                        </span>{' '}
                        on the login screen.
                    </p>
                </div>
            </div>
        ),
    },
    {
        number: 3,
        title: 'Grant Permissions',
        icon: ShieldCheck,
        content: (
            <div className="space-y-3">
                <p className="text-sm text-secondary">
                    The app will request the following permissions — tap{' '}
                    <span className="font-semibold text-foreground">Allow</span>{' '}
                    for each:
                </p>
                <div className="space-y-2">
                    {[
                        {
                            label: 'Bluetooth',
                            detail: 'Required to communicate with your battery Sync Pro.',
                            badge: 'Required',
                        },
                        {
                            label: 'Location',
                            detail: 'Required for Bluetooth scanning.',
                            badge: 'Required',
                        },
                        {
                            label: 'Camera',
                            detail: 'Required only if you need to scan barcodes.',
                            badge: 'Optional',
                        },
                    ].map(({ label, detail, badge }) => (
                        <div
                            key={label}
                            className="flex items-start justify-between gap-3 rounded-lg border border-border p-3"
                        >
                            <div>
                                <p className="text-sm font-medium text-foreground">
                                    {label}
                                </p>
                                <p className="text-sm text-secondary">
                                    {detail}
                                </p>
                            </div>
                            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                                {badge}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-warning/20 p-3 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                    <p className="text-secondary">
                        If you accidentally denied a permission, go to your
                        phone's{' '}
                        <span className="font-semibold text-foreground">
                            Settings &gt; Apps &gt; Sync Pro &gt; Permissions
                        </span>{' '}
                        and enable them manually.
                    </p>
                </div>
            </div>
        ),
    },
    {
        number: 4,
        title: 'Add Your Device',
        icon: Bluetooth,
        content: (
            <div className="space-y-3">
                {[
                    'Make sure Bluetooth is turned on on your phone.',
                    'Stand within 10 feet of your HomeGrid Sync Pro.',
                    'On the Dashboard, tap Add Device.',
                    'Tap Scan — the app will search for nearby Sync Pros.',
                    'When your device appears in the list, tap it to connect.',
                ].map((item, i) => (
                    <div
                        key={i}
                        className="flex items-start gap-2 text-sm text-secondary"
                    >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
                            {i + 1}
                        </span>
                        {item}
                    </div>
                ))}
                <div className="mt-1 flex items-start gap-2 rounded-lg bg-accent/10 p-3 text-sm">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <p className="text-secondary">
                        The Sync Pro will appear by its device name in the scan
                        list. If nothing appears, verify the Sync Pro is powered
                        on and you are close enough to it.
                    </p>
                </div>
            </div>
        ),
    },
    {
        number: 5,
        title: 'Activate Your Device',
        icon: Zap,
        content: (
            <div className="space-y-3">
                <p className="text-sm text-secondary">
                    Once added, tap on your sync pro in the dashboard screen,
                    you will be taken to the{' '}
                    <span className="font-semibold text-foreground">
                        Activate Device
                    </span>{' '}
                    screen. The app checks that your installer has completed all
                    required setup.
                </p>
                <div className="flex items-start gap-2 rounded-lg bg-warning/20 p-3 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                    <p className="text-secondary">
                        If any requirement is not met, the screen will show what
                        is missing. Contact your installer to resolve these
                        before continuing.
                    </p>
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-success/10 p-3 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    <p className="text-secondary">
                        When all requirements are met, you will see the warranty
                        registration form.
                    </p>
                </div>
            </div>
        ),
    },
    {
        number: 6,
        title: 'Register Your Warranty',
        icon: ClipboardList,
        content: (
            <p className="text-sm text-secondary">
                Fill out the warranty registration form with your information
                and submit. After successful submission, you will be taken to
                the{' '}
                <span className="font-semibold text-foreground">
                    Device Detail
                </span>{' '}
                screen where you can monitor your battery system.
            </p>
        ),
    },
    {
        number: 7,
        title: 'Connect to WiFi',
        icon: Wifi,
        badge: 'Recommended',
        content: (
            <div className="space-y-3">
                <p className="text-sm text-secondary">
                    For cloud monitoring and remote updates, connect your Sync
                    Pro to your home WiFi:
                </p>
                {[
                    <span>
                        From the Device Detail screen, tap the{' '}
                        <Cog6ToothIcon className="inline-block h-3.5 w-3.5 align-middle" />{' '}
                        settings icon, then{' '}
                        <span className="font-semibold text-foreground">
                            Set up WiFi
                        </span>
                        .
                    </span>,
                    'The app will scan for available networks via Bluetooth.',
                    'Select your WiFi network from the list.',
                    'Enter your WiFi password and tap Connect.',
                ].map((item, i) => (
                    <div
                        key={i}
                        className="flex items-start gap-2 text-sm text-secondary"
                    >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
                            {i + 1}
                        </span>
                        {item}
                    </div>
                ))}
                <div className="flex items-start gap-2 rounded-lg bg-accent/10 p-3 text-sm">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <p className="text-secondary">
                        Your WiFi password is encrypted before it is sent to the
                        Sync Pro.
                    </p>
                </div>
            </div>
        ),
    },
];

const ONGOING = [
    {
        label: 'Returning to the app',
        detail: 'Your device is saved. Open the app, log in, and tap your device card on the Dashboard to reconnect.',
    },
    {
        label: 'Reconnecting',
        detail: 'Tap your device card and the app will attempt to reconnect automatically over Bluetooth.',
    },
    {
        label: 'Monitoring',
        detail: 'The Device Detail screen shows live battery status, module information, and firmware versions.',
    },
];

const TROUBLESHOOTING = [
    {
        issue: "App can't find my device during scan",
        solution:
            'Ensure Bluetooth is on, you are within ~10 ft of the Sync Pro, and the Sync Pro is powered.',
    },
    {
        issue: '"Device not ready for activation" error',
        solution:
            'Your installer has not completed all setup steps — contact them.',
    },
    {
        issue: 'Bluetooth permission denied',
        solution:
            'Go to phone Settings and enable Bluetooth permission for the app.',
    },
    {
        issue: "Can't log in",
        solution:
            'Verify your credentials, check your internet connection, or tap Contact Support.',
    },
    {
        issue: 'Device disappeared from dashboard',
        solution: 'Tap Add Device and re-scan to reconnect.',
    },
];

const SyncProInstallation = () => {
    return (
        <div className="mx-auto max-w-3xl space-y-6 px-4 py-6">
            {/* Page Header */}
            <Card>
                <CardContent className="space-y-4 pb-4 pt-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">
                            Sync Pro
                        </h1>
                        <p className="text-base text-secondary">
                            Customer Setup Guide
                        </p>
                    </div>
                    <div className="rounded-lg bg-warning/20 p-3 text-sm">
                        <p className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                            Before You Begin
                        </p>
                        <p className="mb-2 text-secondary">
                            Your HomeGrid Sync Pro must be fully installed by a certified installer before you can activate it. The installer must have completed:
                        </p>
                        <ul className="space-y-1">
                            {[
                                'Physical installation of the Sync Pro on the battery system',
                                'Certificate provisioning',
                                'Serial number configuration and verification',
                                'Site information setup',
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-2 text-secondary">
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <p className="mt-2 text-secondary">
                            If your installer has not finished these steps, the app will show an error when you attempt to activate.{' '}
                            <span className="font-semibold text-foreground">Contact your installer before proceeding.</span>
                        </p>
                    </div>
                </CardContent>
            </Card>

            {/* Steps */}
            <div className="space-y-4">
                {STEPS.map(({ number, title, icon: Icon, badge, content }) => (
                    <Card key={number} className="overflow-hidden">
                        <CardHeader className="pb-2 pt-4">
                            <CardTitle className="flex items-center gap-3 text-base font-semibold text-foreground">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                                    {number}
                                </span>
                                <span className="flex items-center gap-2">
                                    <Icon className="h-4 w-4 text-secondary" />
                                    {title}
                                    {badge && (
                                        <span className="rounded-full bg-success/20 px-2 py-0.5 text-xs font-medium text-success">
                                            {badge}
                                        </span>
                                    )}
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pb-4">{content}</CardContent>
                    </Card>
                ))}
            </div>

            {/* Ongoing Use */}
            <Card>
                <CardHeader className="pb-2 pt-4">
                    <CardTitle className="text-base font-semibold text-foreground">
                        Ongoing Use
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pb-4">
                    {ONGOING.map(({ label, detail }) => (
                        <div
                            key={label}
                            className="flex items-start gap-3 text-sm"
                        >
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                            <p className="text-secondary">
                                <span className="font-semibold text-foreground">
                                    {label}:
                                </span>{' '}
                                {detail}
                            </p>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Troubleshooting */}
            <Card>
                <CardHeader className="pb-2 pt-4">
                    <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        Troubleshooting
                    </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                    <div className="overflow-hidden rounded-lg border border-border">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/50">
                                    <th className="px-4 py-2.5 text-left font-semibold text-foreground">
                                        Issue
                                    </th>
                                    <th className="px-4 py-2.5 text-left font-semibold text-foreground">
                                        Solution
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {TROUBLESHOOTING.map(
                                    ({ issue, solution }, i) => (
                                        <tr
                                            key={i}
                                            className={`border-b border-border last:border-0 ${
                                                i % 2 === 0 ? '' : 'bg-muted/20'
                                            }`}
                                        >
                                            <td className="px-4 py-3 font-medium text-foreground">
                                                {issue}
                                            </td>
                                            <td className="px-4 py-3 text-secondary">
                                                {solution}
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
            <HGFooter />
        </div>
    );
};

export default SyncProInstallation;
